import React, { useCallback, useEffect, useRef, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import SpeedRoundedIcon from "@mui/icons-material/SpeedRounded";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import ShoppingCartOutlinedIcon from "@mui/icons-material/ShoppingCartOutlined";
import UploadFileOutlinedIcon from "@mui/icons-material/UploadFileOutlined";
import LayersOutlinedIcon from "@mui/icons-material/LayersOutlined";
import LogoutOutlinedIcon from "@mui/icons-material/LogoutOutlined";
import AppsOutlinedIcon from "@mui/icons-material/AppsOutlined";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import BookmarkBorderOutlinedIcon from "@mui/icons-material/BookmarkBorderOutlined";
import AssignmentTurnedInOutlinedIcon from "@mui/icons-material/AssignmentTurnedInOutlined";
import axios from "axios";
import { Avatar, ThemeProvider } from "@mui/material";
import { materialTrackerTheme } from "../../utils/materialTrackerTheme";
import { getDate } from "../../utils/reportUtils";
import { hasPlannerView } from "../../utils/moduleAccess";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";

const ProcureTrackLayout = ({ badges = {} }) => {
  const navigate = useNavigate();
  const [user, setUser] = useState();
  const storedUser = JSON.parse(localStorage.getItem("user") || "null");

  const USER_NAV = [
    { group: "Plan" },
    {
      to: "dashboard",
      label: "Dashboard",
      icon: <SpeedRoundedIcon fontSize="small" />,
    },
    {
      to: "shortages",
      label: "Shortages",
      icon: <WarningAmberRoundedIcon fontSize="small" />,
      badge: badges.short,
    },
    {
      to: "materials",
      label: "Materials",
      icon: <Inventory2OutlinedIcon fontSize="small" />,
    },
    { group: "Procure" },
    {
      to: "requisitions",
      label: "Requisitions",
      icon: <AssignmentOutlinedIcon fontSize="small" />,
      badge: badges.prPending,
    },
    {
      to: "purchase-orders",
      label: "Purchase orders",
      icon: <ShoppingCartOutlinedIcon fontSize="small" />,
      badge: badges.poOverdue,
    },
    { group: "Data" },
    // {
    //   to: "snapshots",
    //   label: "Snapshots",
    //   icon: <LayersOutlinedIcon fontSize="small" />,
    // },
  ];

  const PLANNER_NAV = [
    { group: "Plan" },
    {
      to: "dashboard",
      label: "Production Plan",
      icon: <SpeedRoundedIcon fontSize="small" />,
    },
    {
      to: "shortages",
      label: "Shortages",
      icon: <WarningAmberRoundedIcon fontSize="small" />,
      badge: badges.short,
    },
    { group: "Allocations" },
    {
      to: "reserves",
      label: "Reserves",
      icon: <BookmarkBorderOutlinedIcon fontSize="small" />,
      badge: badges.reserve,
    },
    {
      to: "allotments",
      label: "Allotments",
      icon: <AssignmentTurnedInOutlinedIcon fontSize="small" />,
      badge: badges.allotted,
    },
    { group: "Sales" },
    {
      to: "sales-reports",
      label: "Sales Reports",
      icon: <CalendarMonthOutlinedIcon fontSize="small" />,
    },
    { group: "Data & Reports" },
    {
      to: "upload",
      label: "Combined MRP + Stock",
      icon: <UploadFileOutlinedIcon fontSize="small" />,
    },
    {
      to: "upload-open-pr",
      label: "Open PR Report",
      icon: <AssignmentOutlinedIcon fontSize="small" />,
    },
    {
      to: "upload-open-po",
      label: "Open PO Report",
      icon: <ShoppingCartOutlinedIcon fontSize="small" />,
    },
    {
      to: "upload-delivery",
      label: "Delivery Report",
      icon: <LocalShippingOutlinedIcon fontSize="small" />,
    },
    {
      to: "production-report",
      label: "Production Report",
      icon: <CalendarMonthOutlinedIcon fontSize="small" />,
    },
  ];

  const NAV = hasPlannerView(storedUser) ? PLANNER_NAV : USER_NAV;

  // Reports shared by the Dashboard, Shortages and Materials pages (same as NPD): the Dashboard
  // always loads them fresh (so it shows every user's uploads and allotments), and Shortages and
  // Materials reuse that copy instead of loading the same reports again.
  const sharedReports = useRef(null);

  const getReports = useCallback((fresh) => {
    if (fresh || !sharedReports.current) {
      const authHeader = {
        headers: { Authorization: `Bearer ${storedUser?.accessToken}` },
      };
      const API = process.env.REACT_APP_BACKEND_URL;
      // India date, not UTC, so the month is correct between 00:00 and 05:30 IST.
      const month = getDate(new Date()).slice(0, 7);
      let uploadError = null;

      const request = Promise.all([
        axios.get(`${API}/mrp/latest`, authHeader),
        axios.get(`${API}/stock/latest`, authHeader),
        axios.get(`${API}/open-pr/latest`, authHeader),
        axios.get(`${API}/open-po/latest`, authHeader),
        axios.get(`${API}/production-report/inventory-adjustment`, authHeader),
        // Requirement is the planner's allotted quantity.
        axios.get(
          `${API}/production-allocation/material-requirement?month=${month}`,
          authHeader,
        ),
        // Receipts after the stock date; a failure must not break the pages.
        axios
          .get(`${API}/delivery-report/latest`, authHeader)
          .catch(() => ({ data: [] })),
        // Optional on Shortages and Materials, required on the Dashboard (uploadError).
        axios
          .get(`${API}/report-upload/latest`, authHeader)
          .catch((e) => {
            uploadError = e;
            return { data: null };
          }),
      ]).then(([m, s, p, o, prd, alt, d, u]) => ({
        m,
        s,
        p,
        o,
        prd,
        alt,
        d,
        u,
        uploadError,
      }));

      sharedReports.current = request;
      // A failed load is not kept, so the next page tries again.
      request.catch(() => {
        if (sharedReports.current === request) sharedReports.current = null;
      });
    }

    return sharedReports.current;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!storedUser?.username) return;

    const fetchUser = async () => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/org-user?username=${storedUser.username}`,
          {
            headers: {
              Authorization: `Bearer ${storedUser.accessToken}`,
            },
          },
        );

        setUser(res.data);
      } catch (error) {
        console.log(error);
      }
    };

    fetchUser();
  }, []);

  useEffect(() => {
    if (!storedUser?.username) return;

    const getOrg = async () => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/org-user/get-org?username=${storedUser.username}`,
          {
            headers: {
              Authorization: `Bearer ${storedUser.accessToken}`,
            },
          },
        );

        if (
          res.data.subscriptionEndDate < new Date().toISOString().split("T")[0]
        ) {
          alert(
            "Your organization's subscription has ended. Please contact the admin.",
          );
          localStorage.clear();
          navigate("/");
          return;
        }

        localStorage.setItem("org", JSON.stringify(res.data.orgId));
      } catch (error) {
        console.error("Error fetching organization:", error);
      }
    };

    getOrg();
  }, []);

  const handleLogout = () => {
    localStorage.clear();
    navigate("/");
  };

  return (
    <ThemeProvider theme={materialTrackerTheme}>
      <div className="pt-app">
        <nav className="sb">
          <div className="sb-logo">
            <Avatar sx={{ bgcolor: "#4338ca", borderRadius: "3px", fontWeight: 600 }} variant="rounded">
              {user?.name
                ?.split(" ")
                .map((n) => n[0])
                .join("") || "PT"}
            </Avatar>

            <div>
              <div className="sb-title">{user?.name || "User"}</div>
              <div className="sb-sub">Coverage &amp; PO tracker</div>
            </div>
          </div>

          <div className="sb-nav">
            {NAV.map((n, idx) =>
              n.group ? (
                <div className="sb-group" key={`g-${idx}`}>
                  {n.group}
                </div>
              ) : (
                <NavLink
                  key={n.to}
                  to={n.to}
                  // Clicking Dashboard always means new data (also for planners, whose dashboard does not load these reports).
                  onClick={() => {
                    if (n.to === "dashboard") sharedReports.current = null;
                  }}
                  className={({ isActive }) =>
                    "sb-item" + (isActive ? " on" : "")
                  }
                >
                  {n.icon}
                  <span>{n.label}</span>
                  {!!n.badge && <span className="sb-badge">{n.badge}</span>}
                </NavLink>
              ),
            )}

            <button
              type="button"
              className="sb-item"
              onClick={() => navigate("/modules")}
            >
              <AppsOutlinedIcon fontSize="small" />
              <span>Modules</span>
            </button>

            <button type="button" className="sb-item" onClick={handleLogout}>
              <LogoutOutlinedIcon fontSize="small" />
              <span>Logout</span>
            </button>
          </div>
        </nav>

        <main className="main">
          <Outlet context={{ getReports }} />
        </main>
      </div>
    </ThemeProvider>
  );
};

export default ProcureTrackLayout;
